import { useCallback, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface CopyButtonProps {
  text: string;
  label: string;
  variant?: 'primary' | 'ghost';
  className?: string;
}

export function CopyButton({ text, label, variant = 'primary', className = '' }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const fallbackRef = useRef<HTMLTextAreaElement>(null);

  const copy = useCallback(async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = fallbackRef.current;
        if (!el) throw new Error('no fallback');
        el.value = text;
        el.select();
        const ok = document.execCommand('copy');
        if (!ok) throw new Error('execCommand failed');
      }
      setState('copied');
      setTimeout(() => setState('idle'), 3000);
    } catch {
      setState('failed');
      const el = fallbackRef.current;
      if (el) {
        el.value = text;
        el.focus();
        el.select();
      }
    }
  }, [text]);

  const base =
    variant === 'primary'
      ? 'w-full sm:w-auto inline-flex items-center justify-center gap-2 min-h-[56px] px-8 py-4 rounded-xl bg-white text-[#1d1d1f] font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(255,255,255,0.15)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0071e3]'
      : 'inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0071e3]';

  return (
    <div className={className}>
      <button type="button" onClick={copy} className={base} aria-live="polite">
        {state === 'copied' ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select and copy below' : label}
      </button>
      <textarea
        ref={fallbackRef}
        readOnly
        tabIndex={state === 'failed' ? 0 : -1}
        aria-hidden={state !== 'failed'}
        className={
          state === 'failed'
            ? 'mt-3 block h-40 w-full rounded-lg border border-white/15 bg-black/60 p-3 font-mono text-xs text-white/80'
            : 'sr-only'
        }
      />
      {state === 'failed' && (
        <p className="mt-2 text-xs text-white/50">
          Your browser blocked the copy action. The text is selected above, so you can copy it manually.
        </p>
      )}
    </div>
  );
}
